import { useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import DropdownButton from '@shared/components/DropdownButton/DropdownButton';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CalendarTime } from 'assets/icons/CalendarTime.svg';
import { ReactComponent as Lock } from 'assets/icons/Lock.svg';
import { ReactComponent as Messages } from 'assets/icons/Messages.svg';
import { ReactComponent as Plus } from 'assets/icons/Plus.svg';
import { ReactComponent as Video } from 'assets/icons/Video.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import { WorkspacePermissionsState } from 'hooks/useWorkspacePermissions/utils';
import i18n from 'i18n/config';
import CreateNewRoomModal from 'modules/CreateNewRoomModal/CreateNewRoomModal';
import CreateNewChatModal from 'modules/Messenger/components/CreateNewChatModal/CreateNewChatModal';
import InstantCallModal from 'modules/VideoMeetings/InstantCallModal/InstantCallModal';
import ScheduleMeetingModal from 'modules/VideoMeetings/ScheduleMeetingModal/ScheduleMeetingModal';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { FeatureFlag } from 'utils/Constants';
import { isGuestModeEnabled } from 'utils/utils';

interface GetOptionsProps {
  onInstantCallClick: () => void;
  onScheduleClick: () => void;
  onPrivateRoomClick: () => void;
  onChatClick: () => void;
  workspacePermissions: WorkspacePermissionsState;
  isGuestUser: boolean;
  isMessengerEnabled: boolean;
}

const defaultIconClassNames = 'stroke-primary-70 shrink-0 w-5 h-5 mr-2';
const getOptions = ({
  onInstantCallClick,
  onScheduleClick,
  onPrivateRoomClick,
  onChatClick,
  workspacePermissions,
  isGuestUser,
  isMessengerEnabled,
}: GetOptionsProps) => {
  const canCreateGroupChat = isMessengerEnabled && workspacePermissions.createRoom && !isGuestUser;
  const options = [];

  if (workspacePermissions.createInstantMeeting) {
    options.push({
      title: i18n.t('instant_call'),
      icon: <Video className={cx(defaultIconClassNames, 'stroke-2')} />,
      onClick: onInstantCallClick,
      selectionClassName: 'mb-2',
      hasDivider: true,
    });
  }

  if (workspacePermissions.createScheduledMeeting) {
    options.push({
      title: i18n.t('schedule_call'),
      icon: <CalendarTime className={cx(defaultIconClassNames, 'stroke-1.5')} />,
      onClick: onScheduleClick,
      selectionClassName: 'mb-2 mt-1',
      hasDivider: true,
    });
  }

  if (workspacePermissions.createPrivateMeeting) {
    options.push({
      title: i18n.t('create_private_room'),
      icon: <Lock className={cx(defaultIconClassNames, 'stroke-1.5')} />,
      onClick: onPrivateRoomClick,
      selectionClassName: cx('mt-1', { 'mb-2': canCreateGroupChat }),
      hasDivider: canCreateGroupChat,
    });
  }

  if (canCreateGroupChat) {
    options.push({
      title: i18n.t('group_chat'),
      icon: <Messages className={cx(defaultIconClassNames, 'stroke-1.5')} />,
      onClick: onChatClick,
      selectionClassName: 'mt-1',
    });
  }

  return options;
};

const CreateNewButton = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { workspacePermissions } = useWorkspacesContext();
  const isMessengerEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER);
  const instantCallModal = useModal(InstantCallModal);
  const scheduleMeetingModal = useModal(ScheduleMeetingModal);
  const createNewRoomModal = useModal(CreateNewRoomModal);
  const createNewChatModal = useModal(CreateNewChatModal);

  const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(matrixClient?.getUserId() ?? '');

  const canCreateAnything =
    workspacePermissions.createInstantMeeting ||
    workspacePermissions.createScheduledMeeting ||
    workspacePermissions.createPrivateMeeting ||
    (isMessengerEnabled && workspacePermissions.createRoom && !isGuestUser);

  if (!canCreateAnything) return null;

  const options = getOptions({
    onInstantCallClick: () => instantCallModal.show(),
    onScheduleClick: () => scheduleMeetingModal.show(),
    onPrivateRoomClick: () => createNewRoomModal.show({ hideScheduling: true }),
    onChatClick: createNewChatModal.show,
    workspacePermissions,
    isGuestUser,
    isMessengerEnabled,
  });

  return (
    <DropdownButton options={options} dropdownContainerClass="w-full" className="min-w-[227px]">
      <Button
        variant="primary"
        size="sm"
        className="hidden md:flex"
        iconLeft={{ icon: Plus, className: 'stroke-current stroke-2' }}
      >
        {t('create_new')}
      </Button>
      <Button variant="primary" size="neutral" className="md:hidden p-[7px] rounded-xl">
        <Plus className="stroke-current stroke-2" />
      </Button>
    </DropdownButton>
  );
};

export default CreateNewButton;
